/**
 * The theme pin for the landing's live demo (item i21806, doc11089).
 *
 * The site names a theme on the demo mount and the demo shows it as designed.
 * The widget resolves each colour as ui, then server config, then the theme's
 * own value, so a colour saved on the demo instance would beat the pinned
 * theme. The pin therefore carries the theme's own values in ui, for the same
 * fields the stills capture blanks to force a theme. Demo bundle only: the
 * customer bundle never reads this.
 */
import { getTheme } from './themes/index.js';

/** The themes a container may pin: concrete ones, never auto or vo-auto. */
export const PINNABLE_THEMES = ['light', 'dark', 'vo-light', 'vo-dark'];

/**
 * The fields a theme only supplies as a fallback. Kept equal to the capture's
 * list (cypress/tasks/display-mode-captures.cjs) by demo-theme.test.js.
 */
export const THEME_DRIVEN_FIELDS = [
  'primaryColor',
  'modalTriggerIconColor',
  'backgroundColor',
  'textColor',
  'inputBackgroundColor',
  'inputTextColor',
  'inputIconColor',
  'inputPlaceholderColor',
  'paginationButtonBgColor',
  'paginationIconColor',
  'borderColor',
  'inputFontSize',
  'inputHeight',
];

/**
 * Parse a theme pin attribute.
 *
 * @param {string|null} value
 * @returns {string|null} a pinnable theme, or null for none or an unknown one
 */
export function parseThemeAttr(value) {
  const theme = String(value ?? '').trim().toLowerCase();
  return PINNABLE_THEMES.includes(theme) ? theme : null;
}

/**
 * The ui overrides that show a pinned theme as designed: the theme itself,
 * plus its own value for every theme-driven field it defines.
 *
 * @param {string} theme - a pinnable theme
 * @returns {Record<string, string>}
 */
export function themePinUi(theme) {
  const values = getTheme(theme);
  const ui = { theme };
  for (const field of THEME_DRIVEN_FIELDS) {
    const value = values[field];
    if (value !== undefined && value !== null && value !== '') ui[field] = value;
  }
  // A theme leaves the pagination button to follow its primary colour, and the
  // widget would otherwise fall back to the server's colours first.
  if (!ui.paginationButtonBgColor) ui.paginationButtonBgColor = ui.primaryColor;
  return ui;
}

/**
 * Accent presets the live demo offers (item i21807), from the site palette:
 * --color-success green and the cyan and purple badge colours.
 */
export const ACCENT_PRESETS = {
  green: '#22c55e',
  cyan: '#22d3ee',
  purple: '#a78bfa',
};

/**
 * The ui overrides for a theme chosen at runtime, optionally with an accent
 * that recolours the primary, highlight and pagination together.
 *
 * @param {string} theme - a pinnable theme
 * @param {string} [accent] - an ACCENT_PRESETS key
 * @returns {Record<string, string>|null} null for an unknown theme or accent
 */
export function themeChoiceUi(theme, accent) {
  if (!PINNABLE_THEMES.includes(theme)) return null;
  if (accent !== undefined && !Object.hasOwn(ACCENT_PRESETS, accent)) return null;
  const ui = themePinUi(theme);
  if (accent === undefined) return ui;
  const colour = ACCENT_PRESETS[accent];
  return { ...ui, primaryColor: colour, highlightColor: colour, paginationButtonBgColor: colour };
}

/**
 * The demo's named looks, in the order the demo shows them (item i22025;
 * reordered by item i22185, decision d11459): the current VO themes carrying
 * the Enter submit button first, then the plain current themes, with the
 * classic VO Search look last for customers who keep it. A custom look is only
 * ever settings a customer can set on their own instance (colors, the input's
 * icon and clear control, result dividers and the submit button), never a
 * customer's actual configuration.
 */
export const DEMO_LOOKS = ['vo-dark-button', 'vo-light-button', 'vo-dark', 'vo-light', 'classic-dark', 'classic-light'];

/** The look every live demo opens on: the first the demo shows. */
export const LEADING_LOOK = DEMO_LOOKS[0];

/** The theme each theme look shows. */
const THEME_LOOKS = { 'vo-dark': 'vo-dark', 'vo-light': 'vo-light', 'classic-dark': 'dark', 'classic-light': 'light' };

/** The Enter submit button the button looks add, in the green accent. */
const ENTER_BUTTON_SETTINGS = {
  primaryColor: ACCENT_PRESETS.green,
  paginationButtonBgColor: ACCENT_PRESETS.green,
  highlightColor: ACCENT_PRESETS.green,
  showClearButton: false,
  submitButtonEnabled: true,
  submitButtonIcon: 'enter',
  submitButtonFill: ACCENT_PRESETS.green,
};

/** Custom looks: a base theme and the settings that change it. */
const CUSTOM_LOOKS = {
  'vo-dark-button': { base: 'vo-dark', settings: ENTER_BUTTON_SETTINGS },
  'vo-light-button': { base: 'vo-light', settings: ENTER_BUTTON_SETTINGS },
};

/**
 * A look with an accent over it (item i22186), as the landing's switcher picks
 * them: the accent recolors the look, a button look's submit button included.
 *
 * @param {string} look - a DEMO_LOOKS name
 * @param {string} [accent] - an ACCENT_PRESETS key
 * @returns {Record<string, *>|null} null for an unknown look or accent
 */
export function lookChoiceUi(look, accent) {
  const ui = lookUi(look);
  if (!ui) return null;
  if (accent === undefined) return ui;
  if (!Object.hasOwn(ACCENT_PRESETS, accent)) return null;
  const colour = ACCENT_PRESETS[accent];
  const recolored = { ...ui, primaryColor: colour, highlightColor: colour, paginationButtonBgColor: colour };
  if (ui.submitButtonEnabled) recolored.submitButtonFill = colour;
  return recolored;
}

/**
 * Parse a look pin attribute (item i22185).
 *
 * @param {string|null} value
 * @returns {string|null} a known look, or null for none or an unknown one
 */
export function parseLookAttr(value) {
  const look = String(value ?? '').trim().toLowerCase();
  return DEMO_LOOKS.includes(look) ? look : null;
}

/**
 * Parse an accent pin attribute (item i22219).
 *
 * @param {string|null} value
 * @returns {string|null} an ACCENT_PRESETS key, or null for none or an unknown one
 */
export function parseAccentAttr(value) {
  const accent = String(value ?? '').trim().toLowerCase();
  return Object.hasOwn(ACCENT_PRESETS, accent) ? accent : null;
}

/**
 * The ui overrides for a named look.
 * @param {string} look - one of DEMO_LOOKS
 * @returns {Record<string, *>|null} null for an unknown look
 */
export function lookUi(look) {
  if (Object.hasOwn(THEME_LOOKS, look)) return themePinUi(THEME_LOOKS[look]);
  if (!Object.hasOwn(CUSTOM_LOOKS, look)) return null;
  const { base, settings } = CUSTOM_LOOKS[look];
  return { ...themePinUi(base), ...settings };
}

/**
 * Custom accents the presets use. Not ACCENT_PRESETS: these are the colours a
 * customer might set on their own instance, picked to read as nobody's purple
 * and, per decision d4824, never green.
 */
const PRESET_ACCENTS = { amber: '#f59e0b', teal: '#14b8a6', red: '#b91c1c', clay: '#b4532a', blue: '#3b82f6', pink: '#e11d74', yellow: '#ffe600', terracotta: '#c2410c', onair: '#e5383b', navy: '#1e3a5f', blaze: '#ff6a00', ocean: '#0e7490' };

/**
 * The Broadsheet design's instance stylesheet: a newspaper search bar, serif
 * type over a heavy black rule with no box and square corners. Literal values
 * and a system serif stack, since a demo host page defines no tokens and the
 * instance rules allow no url() for a web font.
 */
const BROADSHEET_CSS = `
.vo-search-inline { --vo-search-bar-radius: 0; font-family: Georgia, 'Times New Roman', Times, serif; }
.vo-search-search-input-wrapper { border: 0; border-bottom: 4px solid #111; background: transparent; }
.vo-search-search-input-wrapper:focus-within { box-shadow: none; border-bottom-color: #b91c1c; }
.vo-search-search-input { font-family: Georgia, 'Times New Roman', Times, serif; font-size: 24px; font-style: italic; background: transparent; color: #111; }
.vo-search-search-input::placeholder { color: #6b6257; }
.vo-search-search-submit { font-family: Georgia, 'Times New Roman', Times, serif; font-size: 13px; letter-spacing: 0.12em; text-transform: uppercase; border-radius: 0; color: #fbf8f1; }
.vo-search-inline-dropdown { border: 1px solid #111; border-top: 0; border-radius: 0; box-shadow: 4px 4px 0 #111; background: #fbf8f1; color: #111; }
.vo-search-search-result { border-radius: 0; border-bottom: 1px solid #d6cfc2; }
.vo-search-search-result-title { font-family: Georgia, 'Times New Roman', Times, serif; font-size: 19px; font-weight: 700; color: #111; }
.vo-search-search-result-excerpt { font-family: Georgia, 'Times New Roman', Times, serif; color: #3d372f; }
.vo-search-search-result-tag { font-variant: small-caps; letter-spacing: 0.06em; text-transform: lowercase; background: transparent; color: #b91c1c; border: 0; border-radius: 0; padding: 0; }
.vo-search-highlighted { background: transparent; color: inherit; box-shadow: inset 0 -0.35em 0 rgba(185, 28, 28, 0.18); }
`.trim();

/**
 * The Quiet Journal design's instance stylesheet: an in-page panel with no
 * box at all, a large light sans input over a hairline, and soft excerpts
 * spaced like a magazine's contents page.
 */
const QUIET_JOURNAL_CSS = `
.vo-search-inpage, .vo-search-search-modal { --vo-search-bar-radius: 0; background: transparent; border: 0; box-shadow: none; font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif; }
.vo-search-search-header { background: transparent; border-bottom: 0; padding: 8px 0 24px; }
.vo-search-search-body, .vo-search-search-footer { background: transparent; border-top: 0; }
.vo-search-search-input-wrapper { border: 0; border-bottom: 1px solid #d9d2c7; background: transparent; }
.vo-search-search-input-wrapper:focus-within { box-shadow: none; border-bottom-color: #b4532a; }
.vo-search-search-input { font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif; font-size: 30px; font-weight: 300; letter-spacing: -0.01em; color: #2b2723; background: transparent; padding-left: 0; }
.vo-search-search-input::placeholder { color: #b3aa9d; }
.vo-search-search-result:hover { background: transparent; }
.vo-search-search-result-title { font-size: 20px; font-weight: 500; letter-spacing: -0.01em; color: #2b2723; }
.vo-search-search-result-excerpt { font-size: 15px; line-height: 1.6; color: #8a8178; }
.vo-search-search-result-tag { background: transparent; border: 0; padding: 0; color: #b4532a; font-size: 12px; }
.vo-search-highlighted { background: transparent; color: inherit; font-weight: inherit; border-bottom: 1px solid #b4532a; }
`.trim();

/**
 * The Field Manual design's instance stylesheet: a developer-docs command
 * palette. A slate modal, monospace type, compact rows and blue accents.
 */
const FIELD_MANUAL_CSS = `
.vo-search-search-modal-content { background: transparent; border: 0; box-shadow: none; }
.vo-search-search-modal { width: min(560px, 92%); max-height: 70%; background: #1e2430; border: 1px solid #343c4d; border-radius: 10px; box-shadow: 0 24px 64px rgba(0, 0, 0, 0.55); font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace; color: #e5e9f0; }
.vo-search-search-modal.vo-search-active { top: 12%; transform: translate(-50%, 0) scale(1); }
.vo-search-search-titlebar { background: #151a23; border-bottom: 1px solid #343c4d; min-height: 0; padding: 6px 10px; display: flex; align-items: center; }
.vo-search-search-titlebar::before { content: '\\2191\\2193 navigate   \\21B5 open   esc close'; color: #6b7385; font-size: 11px; letter-spacing: 0.04em; margin-right: auto; white-space: pre; }
.vo-search-search-header { background: #1e2430; border-bottom: 1px solid #343c4d; padding: 10px 12px; }
.vo-search-search-body, .vo-search-search-footer { background: #1e2430; border-top-color: #343c4d; }
.vo-search-search-input-wrapper { background: transparent; border: 0; border-radius: 0; }
.vo-search-search-input-wrapper:focus-within { box-shadow: none; }
.vo-search-search-input { font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace; font-size: 15px; color: #e5e9f0; background: transparent; }
.vo-search-search-input::placeholder { color: #6b7385; }
.vo-search-search-result { padding: 8px 12px; border-radius: 6px; border-bottom: 0; }
.vo-search-search-result:hover { background: rgba(59, 130, 246, 0.18); }
.vo-search-search-result:hover .vo-search-search-result-title::after { content: '\\21B5'; float: right; color: #93b4f5; }
.vo-search-search-result-title { font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace; font-size: 13px; font-weight: 600; color: #e5e9f0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.vo-search-search-result-title::before { content: '#'; color: #3b82f6; margin-right: 8px; }
.vo-search-search-result-tags { display: none; }
.vo-search-highlighted { background: rgba(59, 130, 246, 0.22); color: #ffffff; border-radius: 3px; }
.vo-search-search-close-button { font-family: ui-monospace, 'SF Mono', Menlo, Consolas, monospace; font-size: 11px; color: #6b7385; border: 1px solid #343c4d; border-radius: 4px; }
`.trim();

/**
 * The Glossy design's instance stylesheet: a culture magazine's image grid.
 * Black and white, square full-bleed tiles, big condensed uppercase type and a
 * hot pink accent.
 */
const GLOSSY_CSS = `
.vo-search-tile-overlay { --backdrop-color: rgba(255, 255, 255, 0.96); font-family: 'Helvetica Neue', Arial, sans-serif; }
.vo-search-tile-overlay-content { background: #ffffff; border: 0; border-radius: 0; box-shadow: none; color: #0a0a0a; }
.vo-search-search-input-wrapper { border: 0; border-bottom: 3px solid #0a0a0a; border-radius: 0; background: transparent; }
.vo-search-search-input-wrapper:focus-within { box-shadow: none; border-bottom-color: #e11d74; }
.vo-search-search-input { font-family: 'Helvetica Neue', Arial, sans-serif; font-stretch: condensed; font-size: 34px; font-weight: 800; text-transform: uppercase; letter-spacing: -0.01em; color: #0a0a0a; background: transparent; }
.vo-search-search-input::placeholder { color: #a3a3a3; }
.vo-search-tile-card { background: #0a0a0a; border: 0; border-radius: 0; box-shadow: none; }
.vo-search-tile-card:hover { outline: 3px solid #e11d74; outline-offset: -3px; }
.vo-search-tile-hero { border-radius: 0; filter: grayscale(1) contrast(1.1); }
.vo-search-tile-card:hover .vo-search-tile-hero { filter: none; }
.vo-search-tile-body { background: #0a0a0a; color: #ffffff; }
.vo-search-search-result-title { font-family: 'Helvetica Neue', Arial, sans-serif; font-stretch: condensed; font-size: 18px; font-weight: 800; text-transform: uppercase; line-height: 1.05; color: #ffffff; }
.vo-search-search-result-tag { background: #e11d74; color: #ffffff; border: 0; border-radius: 0; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.12em; }
.vo-search-highlighted { background: transparent; color: #e11d74; }
.vo-search-search-close-button { color: #0a0a0a; }
.vo-search-tile-hero { line-height: 0; background-color: #0a0a0a; }
.vo-search-tile-hero-img { display: block; }
`.trim();

/**
 * The Brutalist design's instance stylesheet: a zine's in-page search. Heavy
 * black borders, hard offset shadows, all-caps type and a yellow highlighter.
 */
const BRUTALIST_CSS = `
.vo-search-inpage, .vo-search-search-modal { --vo-search-bar-radius: 0; background: #ffffff; border: 0; box-shadow: none; font-family: 'Arial Black', 'Helvetica Neue', Arial, sans-serif; }
.vo-search-search-header { background: transparent; border-bottom: 0; padding: 4px 0 20px; }
.vo-search-search-body, .vo-search-search-footer { background: transparent; border-top: 0; }
.vo-search-search-input-wrapper { border: 3px solid #000; border-radius: 0; background: #ffffff; box-shadow: 6px 6px 0 #000; }
.vo-search-search-input-wrapper:focus-within { box-shadow: 6px 6px 0 #ffe600, 6px 6px 0 3px #000; }
.vo-search-search-input { font-family: 'Arial Black', 'Helvetica Neue', Arial, sans-serif; font-size: 20px; text-transform: uppercase; color: #000; background: transparent; }
.vo-search-search-input::placeholder { color: #6b6b6b; }
.vo-search-search-submit { border: 3px solid #000; border-radius: 0; text-transform: uppercase; font-weight: 900; letter-spacing: 0.04em; color: #000; }
.vo-search-search-result { border: 3px solid #000; border-radius: 0; margin-bottom: 12px; box-shadow: 4px 4px 0 #000; background: #ffffff; }
.vo-search-search-result:hover { background: #ffffff; transform: translate(-2px, -2px); box-shadow: 6px 6px 0 #000; }
.vo-search-search-result-title { font-family: 'Arial Black', 'Helvetica Neue', Arial, sans-serif; font-size: 18px; text-transform: uppercase; color: #000; }
.vo-search-search-result-excerpt { font-family: 'Courier New', Courier, monospace; color: #000; }
.vo-search-search-result-tag { background: #000; color: #ffe600; border: 0; border-radius: 0; text-transform: uppercase; font-size: 10px; letter-spacing: 0.1em; }
.vo-search-highlighted { background: #ffe600; color: #000; }
`.trim();

/**
 * The Kitchen Table design's instance stylesheet: a recipe blog's in-page photo
 * grid. Warm cream, rounded cards with soft shadows, serif titles, terracotta.
 */
const KITCHEN_TABLE_CSS = `
.vo-search-inpage, .vo-search-search-modal { --vo-search-bar-radius: 999px; background: #fbf3e6; border: 0; box-shadow: none; font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif; }
.vo-search-search-header { background: transparent; border-bottom: 0; padding: 4px 0 20px; }
.vo-search-search-body, .vo-search-search-footer { background: transparent; border-top: 0; }
.vo-search-search-input-wrapper { background: #ffffff; border: 1px solid #ead9c2; border-radius: 999px; box-shadow: 0 2px 8px rgba(120, 72, 32, 0.08); }
.vo-search-search-input-wrapper:focus-within { border-color: #c2410c; box-shadow: 0 0 0 3px rgba(194, 65, 12, 0.18); }
.vo-search-search-input { font-size: 18px; color: #3b2a1e; background: transparent; }
.vo-search-search-input::placeholder { color: #a8927b; }
.vo-search-tile-card { background: #ffffff; border: 0; border-radius: 16px; overflow: hidden; box-shadow: 0 6px 18px rgba(120, 72, 32, 0.12); }
.vo-search-tile-card:hover { box-shadow: 0 10px 26px rgba(120, 72, 32, 0.2); }
.vo-search-tile-hero { border-radius: 0; }
.vo-search-tile-body { color: #3b2a1e; }
.vo-search-search-result-title { font-family: Georgia, 'Times New Roman', Times, serif; font-size: 18px; font-weight: 700; color: #3b2a1e; }
.vo-search-search-result-excerpt { font-size: 14px; color: #7a6450; }
.vo-search-search-result-tag { background: #fde7d6; color: #c2410c; border: 0; border-radius: 999px; font-size: 11px; font-weight: 600; }
.vo-search-highlighted { background: #fde7d6; color: inherit; border-radius: 3px; }
.vo-search-tile-hero { line-height: 0; background-color: #ffffff; }
.vo-search-tile-hero-img { display: block; }
.vo-search-tile-body { height: 112px; overflow: hidden; }
.vo-search-search-result-title { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.vo-search-search-result-excerpt { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
`.trim();

/**
 * The Night Shift design's instance stylesheet: a podcast network's search
 * bar. Warm charcoal, a pill-shaped field, an on-air red dot on every episode,
 * and rows led by square episode art sized through the thumbnail column.
 */
const NIGHT_SHIFT_CSS = `
.vo-search-inline { --vo-search-bar-radius: 999px; font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif; }
.vo-search-search-input-wrapper { background: #26221f; border: 1px solid #45403a; border-radius: 999px; }
.vo-search-search-input-wrapper:focus-within { border-color: #e5383b; box-shadow: 0 0 0 3px rgba(229, 56, 59, 0.25); }
.vo-search-search-input { font-size: 16px; color: #f5efe6; background: transparent; }
.vo-search-search-input::placeholder { color: #9a9085; }
.vo-search-search-submit { border-radius: 999px; font-weight: 700; color: #ffffff; }
.vo-search-inline-dropdown { margin-top: 10px; background: #1b1816; border: 1px solid #45403a; border-radius: 16px; box-shadow: 0 20px 48px rgba(0, 0, 0, 0.6); color: #f5efe6; }
.vo-search-search-result { border-bottom: 1px solid #2e2925; padding: 10px 12px; }
.vo-search-search-result:hover { background: #26221f; }
.vo-search-search-result-link { --vo-result-thumb-width: 64px; align-items: start; }
.vo-search-result-thumbnail { width: 64px; height: 64px; min-height: 64px; align-self: start; border-radius: 10px; object-fit: cover; }
.vo-search-search-result-title { font-size: 15px; font-weight: 700; color: #f5efe6; }
.vo-search-search-result-title::before { content: '\\25CF  '; color: #e5383b; font-size: 10px; vertical-align: 2px; white-space: pre; }
.vo-search-search-result-excerpt { font-size: 13px; color: #b3a99c; }
.vo-search-search-result-tag { background: transparent; color: #e5383b; border: 1px solid #e5383b; border-radius: 999px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; }
.vo-search-highlighted { background: transparent; color: #ff6b6d; }
`.trim();

/**
 * The Quarterly design's instance stylesheet: an academic review's index.
 * In the page, not a box: Roman-numbered entries with hanging indents, italic
 * serif titles in navy, gold rules and small-caps tags.
 */
const QUARTERLY_CSS = `
.vo-search-inpage, .vo-search-search-modal { --vo-search-bar-radius: 0; background: transparent; border: 0; box-shadow: none; font-family: 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif; color: #1e3a5f; }
.vo-search-search-header { background: transparent; border-bottom: 0; padding: 0 0 18px; }
.vo-search-search-body, .vo-search-search-footer { background: transparent; border-top: 0; }
.vo-search-search-input-wrapper { background: transparent; border: 0; border-top: 2px solid #b08d3c; border-bottom: 1px solid #b08d3c; border-radius: 0; }
.vo-search-search-input-wrapper:focus-within { box-shadow: none; border-bottom-color: #1e3a5f; }
.vo-search-search-input { font-family: 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif; font-size: 20px; font-style: italic; color: #1e3a5f; background: transparent; padding-left: 0; }
.vo-search-search-input::placeholder { color: #8a95a5; }
.vo-search-search-results { counter-reset: quarterly; }
.vo-search-search-result { position: relative; padding: 12px 0 12px 3.2em; border: 0; border-radius: 0; counter-increment: quarterly; }
.vo-search-search-result::before { content: counter(quarterly, upper-roman) '.'; position: absolute; left: 0; top: 12px; width: 2.6em; text-align: right; font-family: 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif; font-size: 15px; color: #b08d3c; }
.vo-search-search-result:hover { background: transparent; }
.vo-search-search-result:hover .vo-search-search-result-title { text-decoration: underline; text-decoration-color: #b08d3c; text-underline-offset: 3px; }
.vo-search-search-result-title { font-family: 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif; font-size: 19px; font-style: italic; font-weight: 400; color: #1e3a5f; }
.vo-search-search-result-excerpt { font-family: 'Iowan Old Style', 'Palatino Linotype', Palatino, Georgia, serif; font-size: 14px; line-height: 1.55; color: #4a5568; }
.vo-search-search-result-tag { font-variant: small-caps; text-transform: lowercase; letter-spacing: 0.08em; background: transparent; color: #b08d3c; border: 0; border-radius: 0; padding: 0; }
.vo-search-highlighted { background: transparent; color: inherit; border-bottom: 1px solid #b08d3c; }
`.trim();

/**
 * The Scoreboard design's instance stylesheet: a sports desk's search bar.
 * Black, square, condensed bold uppercase type, and tight ticker rows under a
 * blaze-orange rule.
 */
const SCOREBOARD_CSS = `
.vo-search-inline { --vo-search-bar-radius: 0; font-family: 'Helvetica Neue', Arial, sans-serif; }
.vo-search-search-input-wrapper { background: #111111; border: 2px solid #ffffff; border-radius: 0; }
.vo-search-search-input-wrapper:focus-within { border-color: #ff6a00; box-shadow: none; }
.vo-search-search-input { font-family: 'Helvetica Neue', Arial, sans-serif; font-stretch: condensed; font-size: 18px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.02em; color: #ffffff; background: transparent; }
.vo-search-search-input::placeholder { color: #8a8a8a; }
.vo-search-search-submit { border-radius: 0; font-stretch: condensed; font-weight: 900; text-transform: uppercase; letter-spacing: 0.06em; color: #000000; }
.vo-search-inline-dropdown { background: #0a0a0a; border: 0; border-top: 4px solid #ff6a00; border-radius: 0; box-shadow: 0 16px 40px rgba(0, 0, 0, 0.6); color: #ffffff; }
.vo-search-search-result { padding: 6px 10px; border-bottom: 1px solid #262626; border-radius: 0; }
.vo-search-search-result:hover { background: #1c1c1c; }
.vo-search-search-result-title { font-family: 'Helvetica Neue', Arial, sans-serif; font-stretch: condensed; font-size: 15px; font-weight: 800; text-transform: uppercase; color: #ffffff; }
.vo-search-search-result-tag { background: #ff6a00; color: #000000; border: 0; border-radius: 0; font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; }
.vo-search-highlighted { background: transparent; color: #ff6a00; }
`.trim();

/**
 * The Coastline design's instance stylesheet: a travel guide's image grid. Sand
 * and ocean blue, soft rounded tiles with gentle shadows, pill tags.
 */
const COASTLINE_CSS = `
.vo-search-tile-overlay { --backdrop-color: rgba(14, 116, 144, 0.35); font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif; }
.vo-search-tile-overlay-content { background: #f5ecdc; border: 0; border-radius: 24px; box-shadow: 0 24px 60px rgba(14, 64, 84, 0.3); color: #17384a; }
.vo-search-search-input-wrapper { background: #efe3cc; border: 1px solid #e0cfae; border-radius: 999px; box-shadow: none; }
.vo-search-search-input-wrapper:focus-within { box-shadow: 0 0 0 3px rgba(14, 116, 144, 0.3); }
.vo-search-search-input { font-size: 18px; color: #17384a; background: transparent; }
.vo-search-search-input::placeholder { color: #8aa3ad; }
.vo-search-tile-card { background: #ffffff; border: 0; border-radius: 20px; overflow: hidden; box-shadow: 0 8px 22px rgba(14, 64, 84, 0.14); }
.vo-search-tile-card:hover { transform: translateY(-2px); box-shadow: 0 14px 30px rgba(14, 64, 84, 0.2); }
.vo-search-tile-hero { border-radius: 0; filter: saturate(1.1); }
.vo-search-tile-body { color: #17384a; }
.vo-search-search-result-title { font-size: 16px; font-weight: 700; color: #17384a; }
.vo-search-search-result-excerpt { font-size: 13px; color: #5b7884; }
.vo-search-search-result-tag { background: #dff1f5; color: #0e7490; border: 0; border-radius: 999px; font-size: 11px; font-weight: 600; padding: 2px 10px; }
.vo-search-highlighted { background: #dff1f5; color: inherit; border-radius: 3px; }
.vo-search-search-close-button { color: #17384a; }
.vo-search-tile-hero { line-height: 0; background-color: #ffffff; }
.vo-search-tile-hero-img { display: block; }
`.trim();

/**
 * Named presets of real instance settings (item i22491, decision d11705), so
 * a first-party demo can show the widget heavily customized. Demo bundle only.
 *
 * Each is an optional base theme, an optional render shape (a shape-changing
 * preset remounts, because shape is read only at mount) and the settings.
 *
 * film-panel caps the in-page panel at the rows it shows whole (decision
 * d11704). The in-page surface pages by resultsPerPage; inlineMaxResults caps
 * only the attached dropdown, so the cap is a page size with no pager.
 */
export const DEMO_PRESETS = {
  'film-panel': {
    settings: { resultsPerPage: 5, enablePagination: false },
  },
  'editorial-gallery': {
    base: 'vo-light',
    shape: { trigger: 'none', surface: 'inpage', layout: 'grid' },
    settings: {
      primaryColor: PRESET_ACCENTS.amber,
      highlightColor: PRESET_ACCENTS.amber,
      paginationButtonBgColor: PRESET_ACCENTS.amber,
      tileColumns: 3,
      showHeroImage: true,
      showThumbnails: true,
      showExcerpts: true,
      displayTags: true,
      inputFontSize: '20px',
      inputHeight: '56px',
      searchPlaceholder: 'Search the archive',
    },
  },
  'compact-newsroom': {
    base: 'dark',
    shape: { trigger: 'bar', surface: 'attached', layout: 'list' },
    settings: {
      primaryColor: PRESET_ACCENTS.teal,
      highlightColor: PRESET_ACCENTS.teal,
      paginationButtonBgColor: PRESET_ACCENTS.teal,
      displayTags: true,
      showAccessIcons: true,
      resultDividers: false,
      resultRowPadding: '6px 12px',
      showClearButton: false,
      submitButtonEnabled: true,
      submitButtonLabel: 'Go',
      submitButtonFill: PRESET_ACCENTS.teal,
    },
  },
  broadsheet: {
    base: 'light',
    shape: { trigger: 'bar', surface: 'attached', layout: 'list' },
    settings: {
      primaryColor: PRESET_ACCENTS.red,
      highlightColor: PRESET_ACCENTS.red,
      paginationButtonBgColor: PRESET_ACCENTS.red,
      displayTags: true,
      resultDividers: true,
      searchPlaceholder: 'Search the archive',
      submitButtonEnabled: true,
      submitButtonLabel: 'Search',
      submitButtonFill: '#111111',
      customCss: BROADSHEET_CSS,
    },
  },
  'quiet-journal': {
    base: 'light',
    shape: { trigger: 'none', surface: 'inpage', layout: 'list' },
    settings: {
      primaryColor: PRESET_ACCENTS.clay,
      highlightColor: PRESET_ACCENTS.clay,
      paginationButtonBgColor: PRESET_ACCENTS.clay,
      resultsPerPage: 3,
      enablePagination: false,
      resultDividers: false,
      showExcerpts: true,
      showClearButton: false,
      showInputIcon: false,
      submitButtonEnabled: false,
      resultRowPadding: '18px 0',
      searchPlaceholder: 'What are you looking for?',
      customCss: QUIET_JOURNAL_CSS,
    },
  },
  'field-manual': {

    base: 'dark',
    shape: { trigger: 'icon', surface: 'overlay', layout: 'list' },
    settings: {
      primaryColor: PRESET_ACCENTS.blue,
      highlightColor: PRESET_ACCENTS.blue,
      paginationButtonBgColor: PRESET_ACCENTS.blue,
      modalTriggerIconColor: PRESET_ACCENTS.blue,
      showExcerpts: false,
      showClearButton: false,
      submitButtonEnabled: false,
      displayTags: true,
      resultDividers: false,
      searchPlaceholder: 'Search the docs',
      customCss: FIELD_MANUAL_CSS,
    },
  },
  'glossy': {

    base: 'light',
    shape: { trigger: 'icon', surface: 'overlay', layout: 'grid' },
    settings: {
      primaryColor: PRESET_ACCENTS.pink,
      highlightColor: PRESET_ACCENTS.pink,
      paginationButtonBgColor: '#0a0a0a',
      modalTriggerIconColor: '#0a0a0a',
      tileColumns: 3,
      showHeroImage: true,
      showThumbnails: true,
      showExcerpts: false,
      displayTags: true,
      searchPlaceholder: 'Search the issue',
      customCss: GLOSSY_CSS,
    },
  },
  'brutalist': {

    base: 'light',
    shape: { trigger: 'none', surface: 'inpage', layout: 'list' },
    settings: {
      primaryColor: '#000000',
      highlightColor: '#000000',
      paginationButtonBgColor: '#000000',
      resultsPerPage: 3,
      enablePagination: false,
      resultDividers: false,
      showExcerpts: true,
      displayTags: true,
      showClearButton: false,
      showInputIcon: false,
      submitButtonEnabled: true,
      submitButtonLabel: 'Find it',
      submitButtonFill: PRESET_ACCENTS.yellow,
      searchPlaceholder: 'Search the zine',
      customCss: BRUTALIST_CSS,
    },
  },
  'kitchen-table': {

    base: 'light',
    shape: { trigger: 'none', surface: 'inpage', layout: 'grid' },
    settings: {
      primaryColor: PRESET_ACCENTS.terracotta,
      highlightColor: PRESET_ACCENTS.terracotta,
      paginationButtonBgColor: PRESET_ACCENTS.terracotta,
      tileColumns: 3,
      resultsPerPage: 3,
      enablePagination: false,
      showHeroImage: true,
      showThumbnails: true,
      showExcerpts: true,
      displayTags: true,
      showClearButton: false,
      submitButtonEnabled: false,
      searchPlaceholder: 'Search recipes',
      customCss: KITCHEN_TABLE_CSS,
    },
  },
  'night-shift': {

    base: 'dark',
    shape: { trigger: 'bar', surface: 'attached', layout: 'list' },
    settings: {
      primaryColor: PRESET_ACCENTS.onair,
      highlightColor: PRESET_ACCENTS.onair,
      paginationButtonBgColor: PRESET_ACCENTS.onair,
      showThumbnails: true,
      showExcerpts: true,
      displayTags: true,
      showClearButton: false,
      submitButtonEnabled: true,
      submitButtonLabel: 'Listen',
      submitButtonFill: PRESET_ACCENTS.onair,
      searchPlaceholder: 'Search episodes',
      customCss: NIGHT_SHIFT_CSS,
    },
  },
  'quarterly': {

    base: 'light',
    shape: { trigger: 'none', surface: 'inpage', layout: 'list' },
    settings: {
      primaryColor: PRESET_ACCENTS.navy,
      highlightColor: PRESET_ACCENTS.navy,
      paginationButtonBgColor: PRESET_ACCENTS.navy,
      showExcerpts: true,
      displayTags: true,
      resultDividers: true,
      showClearButton: false,
      showInputIcon: false,
      submitButtonEnabled: false,
      resultsPerPage: 4,
      enablePagination: false,
      searchPlaceholder: 'Search the journal',
      customCss: QUARTERLY_CSS,
    },
  },
  'scoreboard': {

    base: 'dark',
    shape: { trigger: 'bar', surface: 'attached', layout: 'list' },
    settings: {
      primaryColor: PRESET_ACCENTS.blaze,
      highlightColor: PRESET_ACCENTS.blaze,
      paginationButtonBgColor: PRESET_ACCENTS.blaze,
      showExcerpts: false,
      displayTags: true,
      resultDividers: true,
      showClearButton: false,
      submitButtonEnabled: true,
      submitButtonLabel: 'Go',
      submitButtonFill: PRESET_ACCENTS.blaze,
      searchPlaceholder: 'Teams, players, scores',
      customCss: SCOREBOARD_CSS,
    },
  },
  'coastline': {

    base: 'light',
    shape: { trigger: 'icon', surface: 'overlay', layout: 'grid' },
    settings: {
      primaryColor: PRESET_ACCENTS.ocean,
      highlightColor: PRESET_ACCENTS.ocean,
      paginationButtonBgColor: PRESET_ACCENTS.ocean,
      modalTriggerIconColor: PRESET_ACCENTS.ocean,
      tileColumns: 3,
      showHeroImage: true,
      showThumbnails: true,
      showExcerpts: true,
      displayTags: true,
      searchPlaceholder: 'Where to next?',
      customCss: COASTLINE_CSS,
    },
  },
};

/**
 * The "Make it yours" showcase designs (decision d11713), in the order the
 * showcase shows them. Each is a whole look: its own shape and stylesheet.
 */
export const DESIGN_PRESETS = ['broadsheet', 'quiet-journal', 'field-manual', 'glossy', 'brutalist', 'kitchen-table', 'night-shift', 'quarterly', 'scoreboard', 'coastline'];

/**
 * Parse a preset pin attribute.
 *
 * @param {string|null|undefined} value
 * @returns {string|null} a DEMO_PRESETS name, or null for none or an unknown one
 */
export function parsePresetAttr(value) {
  const preset = String(value ?? '').trim().toLowerCase();
  return Object.hasOwn(DEMO_PRESETS, preset) ? preset : null;
}

/** True for an exact DEMO_PRESETS name, never an inherited Object key. */
function isPreset(name) {
  return typeof name === 'string' && Object.hasOwn(DEMO_PRESETS, name);
}

/**
 * The ui overrides for a named preset: its base theme, then its settings.
 *
 * @param {string} name - a DEMO_PRESETS name
 * @returns {Record<string, *>|null} null for an unknown preset
 */
export function presetUi(name) {
  if (!isPreset(name)) return null;
  const { base, settings } = DEMO_PRESETS[name];
  return { ...(base ? themePinUi(base) : {}), ...settings };
}

/**
 * The render shape a preset pins.
 *
 * @param {string} name - a DEMO_PRESETS name
 * @returns {{trigger: string, surface: string, layout: string}|null} null for
 *   an unknown preset or one that keeps the container's shape
 */
export function presetShape(name) {
  if (!isPreset(name)) return null;
  const { shape } = DEMO_PRESETS[name];
  return shape ? { ...shape } : null;
}

/**
 * The parts of a result a demo can show or hide (item i22906), each with the
 * widget setting that draws it. The landing pins all four and the Customize
 * look popover switches them; a customer sets the same four on their own
 * instance.
 */
export const RESULT_PARTS = {
  thumbnails: 'showThumbnails',
  excerpts: 'showExcerpts',
  icons: 'showAccessIcons',
  tags: 'displayTags',
};

/**
 * Parse a result-parts pin: a comma list of RESULT_PARTS names.
 *
 * @param {string|null|undefined} value
 * @returns {string[]|null} the known parts named, or null for no pin (the
 *   server decides)
 */
export function parseResultPartsAttr(value) {
  if (value === null || value === undefined) return null;
  return String(value)
    .split(',')
    .map((part) => part.trim().toLowerCase())
    .filter((part) => Object.hasOwn(RESULT_PARTS, part));
}

/**
 * The widget settings for a result-parts pin: every named part on, every
 * other part off.
 *
 * @param {string[]|null} parts
 * @returns {Record<string, boolean>} empty for no pin
 */
export function resultPartsUi(parts) {
  if (!parts) return {};
  return Object.fromEntries(Object.entries(RESULT_PARTS).map(([part, setting]) => [setting, parts.includes(part)]));
}

/**
 * The widget settings for parts switched at runtime.
 *
 * @param {Record<string, boolean>} parts - RESULT_PARTS names to on or off
 * @returns {Record<string, boolean>|null} null for an unknown part or a
 *   value that is not a boolean
 */
export function resultPartsChoiceUi(parts) {
  const entries = Object.entries(parts ?? {});
  if (entries.some(([part, on]) => !Object.hasOwn(RESULT_PARTS, part) || typeof on !== 'boolean')) return null;
  return Object.fromEntries(entries.map(([part, on]) => [RESULT_PARTS[part], on]));
}
